import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import useNavbarItems from "./useNavbarItems";
import {CustomNavLink} from "./NavbarStyledComponents";
import {RoutePaths} from "../../util/constants";
import genie_logo_short from "../../assets/images/logo-short.svg";
import genie_logo from "../../assets/images/logo.svg";
import {Link, useLocation} from "react-router-dom";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {alpha, Divider, Drawer, IconButton, Theme, Tooltip, useMediaQuery,} from "@mui/material";
import LanguageDropdown from "../shared/language-dropdown/LanguageDropdown";
import MenuIcon from "@mui/icons-material/Menu";
import DrawerList from "../shared/drawer-list/DrawerList";
import {RootState, useAppSelector} from "../../store";
import {NotificationsOutlined} from "@mui/icons-material";
import Notifications from "./notifications/Notifications";
import Badge from "@mui/material/Badge";
import ProfileDropdown from "../shared/profile-dropdown/ProfileDropdown";
import {useTheme} from "@mui/material/styles";
import HideOnScroll from "./hide-on-scroll/HideOnScroll";
import ScanWinnerDialog from "../dialogs/scan-winner-dialog/ScanWinnerDialog";
import ValidateVoucherDialog from "../dialogs/validate-voucher-dialog/ValidateVoucherDialog";
import genie_points from "../../assets/images/genie-points-short-black.svg";
import {formatNumber} from "../../util/utils";
import backgroundImage from "../../assets/images/background.jpg";

const Navbar = () => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState<null | HTMLElement>(null);
    const { t } = useTranslation();

    const token = useAppSelector((state: RootState) => state.authReducer.loggedInUser.access_token);
    const isAdmin = useAppSelector((state: RootState) => state.authReducer.loggedInUser.is_admin);
    const isClient = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.client_id);
    const availablePoints = useAppSelector(
        (state: RootState) => state.authReducer.loggedInUser.available_points
    );
    const notifications = useAppSelector((state: RootState) => state.notificationReducer.items);
    const [menuItems, exploreMoreItems] = useNavbarItems();

    const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
    const matchesMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
    const [openScanWinnerDialog, setOpenScanWinnerDialog] = useState(false);
    const [openValidateVoucherDialog, setOpenValidateVoucherDialog] = useState(false);
    const theme = useTheme();
    const {pathname, search, hash} = useLocation();
    const drawerBackground = `url(${backgroundImage})`;

    const unreadNotifications = useMemo(
        () => notifications.filter((n) => !n.read).length,
        [notifications]
    );

    const toggleDrawer = () => setOpenDrawer((old) => !old);

    const handleOpenNotificationsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setNotificationsAnchorEl(event.currentTarget);
    };

    const handleDrawerButtonClick = useCallback(() => {
        if (isAdmin) {
            setOpenScanWinnerDialog(true);
            return;
        }
        if (isClient) {
            setOpenValidateVoucherDialog(true);
        }
    }, [isAdmin, isClient]);

    return (
        <HideOnScroll>
            <AppBar
                position="fixed"
                color="transparent"
                elevation={0}
                sx={{
                    backgroundColor: alpha(theme.palette.secondary.main, 0.6),
                    backdropFilter: "blur(5px)",
                    zIndex: theme.zIndex.appBar,
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        overflowX: "auto",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    <Toolbar disableGutters>
                        {/* Mobile Menu */}
                        <Box sx={{ mr: 1, my: 0.5, display: { xs: "flex", md: "none" } }}>
                            <IconButton
                                size="large"
                                aria-label="menu"
                                onClick={toggleDrawer}
                                color="primary"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Drawer
                                anchor="left"
                                variant="temporary"
                                color="secondary"
                                open={openDrawer}
                                onClose={toggleDrawer}
                                ModalProps={{ keepMounted: true }}
                                slotProps={{
                                    backdrop: {
                                        sx: {
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.6),
                                            backdropFilter: "blur(2px)",
                                        },
                                    },
                                }}
                                sx={{
                                    display: { xs: "block", md: "none" },
                                    "& .MuiDrawer-paper": {
                                        boxSizing: "border-box",
                                        width: 260,
                                        backgroundColor: theme.palette.secondary.main,
                                        backgroundImage: drawerBackground,
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: "cover",
                                    },
                                }}
                            >
                                <DrawerList
                                    drawerListItems={menuItems}
                                    exploreMoreListItems={exploreMoreItems}
                                    handleButtonClick={handleDrawerButtonClick}
                                    toggleDrawer={toggleDrawer}
                                />
                            </Drawer>
                        </Box>

                        {/* Logo */}
                        <Box mr={2} sx={{ flexGrow: { xs: 1, md: 0 } }}>
                            <Link
                                to={RoutePaths.HOME}
                                style={{
                                    display: !matchesSm ? "inherit" : "flex",
                                    width: "fit-content",
                                }}
                            >
                                <img
                                    src={matchesSm ? genie_logo_short : genie_logo}
                                    width={matchesSm ? 24 : 100}
                                    alt="genie-logo"
                                />
                            </Link>
                        </Box>

                        {/* Desktop Menu */}
                        <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                            {menuItems.map((page) => (
                                <CustomNavLink
                                    key={page.routeTo}
                                    to={page.routeTo}
                                >
                                    {page.label}
                                </CustomNavLink>
                            ))}
                        </Box>

                        {/* Right-side actions */}
                        <Box display="flex" gap={1} alignItems="center">
                            {!matchesMd && <LanguageDropdown />}

                            {token.length > 0 && !isAdmin && !isClient && (
                                <IconButton color="primary" onClick={handleOpenNotificationsMenu}>
                                    <Badge badgeContent={unreadNotifications} max={9} color="primary">
                                        <NotificationsOutlined color="primary" />
                                    </Badge>
                                </IconButton>
                            )}

                            {token.length > 0 && !isAdmin && !isClient && (
                                <Tooltip title={t("points_to_use_tooltip")} arrow enterTouchDelay={0}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        endIcon={<img src={genie_points} alt="Genie points" height={18} />}
                                        sx={{
                                            minWidth: "fit-content",
                                            fontWeight: 600,
                                            "& > span": { ml: "2px" },
                                        }}
                                        component={Link}
                                        to={RoutePaths.WISHES}

                                    >
                                        {formatNumber(availablePoints ?? 0)}
                                    </Button>
                                </Tooltip>
                            )}

                            {token.length === 0 ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    component={Link}
                                    to={RoutePaths.AUTH}
                                    state={{redirectTo: `${pathname}${search}${hash}`}}
                                >
                                    {t("login_register")}
                                </Button>
                            ) : (
                                <>
                                    <ProfileDropdown />
                                    <Notifications
                                        anchorEl={notificationsAnchorEl}
                                        setAnchorEl={setNotificationsAnchorEl}
                                    />
                                </>
                            )}
                        </Box>
                    </Toolbar>

                    {isAdmin && (
                        <ScanWinnerDialog
                            open={openScanWinnerDialog}
                            handleDialogClose={() => setOpenScanWinnerDialog(false)}
                        />
                    )}
                    {isClient && (
                        <ValidateVoucherDialog
                            open={openValidateVoucherDialog}
                            handleDialogClose={() => setOpenValidateVoucherDialog(false)}
                        />
                    )}
                </Container>

                <Divider
                    sx={{
                        background: alpha(theme.palette.secondary.main, 0.5),
                        height: "1px",
                    }}
                />
            </AppBar>
        </HideOnScroll>
    );
};

export default Navbar;
